import { createSelector } from '@reduxjs/toolkit';
import { DataStatus, isFinished, isIdle, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as ProductsAPI from '../api/productsAPI';
import type {
  ApplicationPlatform,
  Product,
  ProductApplication,
  ProductWebfunnelLink,
  WebLinkType,
  WebApplicationType,
  ProductWebhook,
} from './types';

interface State {
  products:
    | {
        data: Product[];
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
  webfunnelLinks:
    | {
        data: ProductWebfunnelLink[];
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
}

const initialState: State = {
  products: {
    data: null,
    status: DataStatus.idle,
  },
  webfunnelLinks: {
    data: null,
    status: DataStatus.idle,
  },
};

export const productsSlice = createAppSlice({
  name: 'products',
  initialState,
  reducers: (create) => ({
    setupProductApplications: create.asyncThunk<
      { productId: string },
      ProductApplication[]
    >(
      (params) => {
        return ProductsAPI.getApplications(params);
      },
      {
        fulfilled: (state, action) => {
          if (!state.products.data) {
            return;
          }

          state.products.data = state.products.data.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                applications: action.payload,
                applicationsDataStatus: DataStatus.finished,
              };
            }

            return product;
          });
        },
        rejected: (state, action) => {
          if (!state.products.data) {
            return;
          }

          state.products.data = state.products.data.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                applicationsDataStatus: DataStatus.error,
              };
            }

            return product;
          });
        },
        options: {
          condition: ({ productId }, { getState }) => {
            const state = getState() as RootState;
            const product = state.products.products.data?.find(
              (product) => product.id === productId,
            );

            if (!product || isFinished(product.applicationsDataStatus)) {
              return false;
            }
          },
        },
      },
    ),
    setupProducts: create.asyncThunk(
      (_: void) => {
        return ProductsAPI.getProducts();
      },
      {
        pending: (state) => {
          state.products.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.products.data = action.payload;
          state.products.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.products.status = DataStatus.error;
        },
      },
    ),
    addProduct: create.asyncThunk<{ name: string }, Product>(
      (product) => {
        return ProductsAPI.addProduct(product);
      },
      {
        fulfilled: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          state.products.data.push(action.payload);
        },
      },
    ),
    updateProductApplications: create.asyncThunk<
      { id: string; integrationId: string; productId: string }[],
      void
    >(
      (params) => {
        return ProductsAPI.updateProductApplications({
          updates: params,
        });
      },
      {
        fulfilled: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          const updates = action.meta.arg;

          state.products.data = state.products.data.map((product) => {
            const productUpdates = updates.filter(
              (u) => u.productId === product.id,
            );

            if (productUpdates.length === 0) {
              return product;
            }

            return {
              ...product,
              applications: product.applications.map((application) => {
                const update = productUpdates.find(
                  (u) => u.id === application.id,
                );
                return update
                  ? { ...application, integrationId: update.integrationId }
                  : application;
              }),
            };
          });
        },
      },
    ),
    addProductApplication: create.asyncThunk<
      | {
          productId: string;
          integrationId: string;
          platform: ApplicationPlatform.ios;
          bundleId: string;
          appstoreId: string;
          name: string;
          logoUrl: string;
        }
      | {
          productId: string;
          integrationId: string;
          platform: ApplicationPlatform.android;
          bundleId: string;
          name: string;
          logoUrl: string;
        }
      | {
          productId: string;
          platform: ApplicationPlatform.web;
          webUrl: string;
          applicationType: WebApplicationType;
          name: string;
        },
      ProductApplication
    >(
      (params) => {
        return ProductsAPI.addProductApplication(params);
      },
      {
        fulfilled: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          const product = state.products.data.find(
            (product) => product.id === action.payload.productId,
          );

          if (!product) {
            return;
          }

          const newApplications = [...product.applications, action.payload];
          const newApplicationPlatforms = Array.from(
            new Set(newApplications.map((application) => application.platform)),
          );

          product.applications = newApplications;
          product.applicationPlatforms = newApplicationPlatforms;
        },
      },
    ),
    deleteProductApplication: create.asyncThunk<
      { productId: string; id: string },
      void
    >(
      (params) => {
        return ProductsAPI.deleteProductApplication(params);
      },
      {
        fulfilled: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          state.products.data = state.products.data.map((product) => {
            if (product.id === action.meta.arg.productId) {
              const newApplications = product.applications.filter(
                (application) => application.id !== action.meta.arg.id,
              );

              return {
                ...product,
                applicationPlatforms: Array.from(
                  new Set(
                    newApplications.map((application) => application.platform),
                  ),
                ),
                applications: newApplications,
              };
            }

            return product;
          });
        },
      },
    ),
    setupWebfunnelLinks: create.asyncThunk(
      (_: void) => {
        return ProductsAPI.getWebfunnelLinks();
      },
      {
        pending: (state) => {
          state.webfunnelLinks.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          state.webfunnelLinks.data = action.payload;
          state.webfunnelLinks.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.webfunnelLinks.status = DataStatus.error;
          state.webfunnelLinks.data = null;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (
              isLoading(state.products.webfunnelLinks.status) ||
              isFinished(state.products.webfunnelLinks.status)
            ) {
              return false;
            }
          },
        },
      },
    ),
    addWebfunnelLink: create.asyncThunk<
      { productId: string; webUrl: string; linkType: WebLinkType },
      ProductWebfunnelLink
    >(
      (params) => {
        return ProductsAPI.addWebfunnelLink(params);
      },
      {
        fulfilled: (state, action) => {
          if (!state.webfunnelLinks.data) {
            return;
          }

          state.webfunnelLinks.data.push(action.payload);
        },
      },
    ),
    deleteWebfunnelLink: create.asyncThunk<
      { productId: string; id: string },
      void
    >(
      (params) => {
        return ProductsAPI.deleteWebfunnelLink(params);
      },
      {
        fulfilled: (state, action) => {
          if (!state.webfunnelLinks.data) {
            return;
          }

          state.webfunnelLinks.data = state.webfunnelLinks.data.filter(
            (link) => link.id !== action.meta.arg.id,
          );
        },
      },
    ),
    updateWebfunnelLink: create.asyncThunk<
      { productId: string; id: string; webUrl: string; linkType: WebLinkType },
      ProductWebfunnelLink
    >(
      (params) => {
        return ProductsAPI.updateWebfunnelLink(params);
      },
      {
        fulfilled: (state, action) => {
          if (!state.webfunnelLinks.data) {
            return;
          }

          state.webfunnelLinks.data = state.webfunnelLinks.data.map((link) => {
            if (link.id === action.payload.id) {
              return action.payload;
            }

            return link;
          });
        },
      },
    ),
    setupProductWebhooks: create.asyncThunk<
      { productId: string },
      ProductWebhook[]
    >(
      (params) => {
        return ProductsAPI.getWebhooks(params);
      },
      {
        pending: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          state.products.data = state.products.data.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                webhooksDataStatus: DataStatus.loading,
              };
            }

            return product;
          });
        },
        fulfilled: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          state.products.data = state.products.data.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                webhooks: action.payload,
                webhooksDataStatus: DataStatus.finished,
              };
            }

            return product;
          });
        },
        options: {
          condition: ({ productId }, { getState }) => {
            const state = getState() as RootState;

            if (!isProductsFinished(state.products)) {
              return false;
            }

            const product = state.products.products.data.find(
              (product) => product.id === productId,
            );

            if (
              !product ||
              isLoading(product.webhooksDataStatus) ||
              isFinished(product.webhooksDataStatus)
            ) {
              return false;
            }
          },
        },
      },
    ),
    addProductWebhook: create.asyncThunk<
      {
        productId: string;
        sourceIntegrationId: string;
        webhookPath: string;
        appHash: string;
        applicationIds: string[];
      },
      ProductWebhook
    >(
      (params) => {
        return ProductsAPI.addWebhook(params);
      },
      {
        fulfilled: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          state.products.data = state.products.data.map((product) => {
            if (product.id === action.payload.productId) {
              return {
                ...product,
                webhooks: [...product.webhooks, action.payload],
              };
            }

            return product;
          });
        },
      },
    ),
    deleteProductWebhook: create.asyncThunk<
      { productId: string; id: string },
      void
    >(
      (params) => {
        return ProductsAPI.deleteWebhook(params);
      },
      {
        fulfilled: (state, action) => {
          if (!isProductsFinished(state)) {
            return;
          }

          state.products.data = state.products.data.map((product) => {
            if (product.id === action.meta.arg.productId) {
              return {
                ...product,
                webhooks: product.webhooks.filter(
                  (webhook) => webhook.id !== action.meta.arg.id,
                ),
              };
            }

            return product;
          });
        },
      },
    ),
    reset: create.reducer(() => {
      return { ...initialState };
    }),
  }),
  selectors: {
    selectProducts: createSelector(
      (state: State) => state.products.data,
      (data) => {
        return data || [];
      },
    ),
    selectProductsDataStatus: (state) => {
      return state.products.status;
    },
    selectIsProductsLoading: (state) => {
      return isIdle(state.products.status) || isLoading(state.products.status);
    },
    selectIsProductsFinished: (state) => {
      return isFinished(state.products.status);
    },
    selectIsProductApplicationsLoading: createSelector(
      (state) => state.products.data,
      (_, productId: string) => {
        return productId;
      },
      (products: Product[], productId: string) => {
        const product = products?.find(
          (product: Product) => product.id === productId,
        );

        if (!product) {
          return false;
        }

        return (
          isLoading(product.applicationsDataStatus) ||
          isIdle(product.applicationsDataStatus)
        );
      },
    ),
    selectProductApplications: createSelector(
      (state) => state.products.data,
      (_, productId: string) => {
        return productId;
      },
      (products: Product[], productId: string) => {
        return (
          products?.find((product: Product) => product.id === productId)
            ?.applications || []
        );
      },
    ),
    selectProductWebfunnelLinks: createSelector(
      (state) => state.webfunnelLinks.data,
      (_, productId: string) => {
        return productId;
      },
      (webfunnelLinks: ProductWebfunnelLink[], productId: string) => {
        return (
          webfunnelLinks?.filter(
            (link: ProductWebfunnelLink) => link.productId === productId,
          ) || []
        );
      },
    ),
    selectAllWebfunnelLinks: (state) => {
      return state.webfunnelLinks.data || [];
    },
    selectWebfunnelLinksDataStatus: (state) => {
      return state.webfunnelLinks.status;
    },
    selectProductWebhooks: createSelector(
      (state) => state.products.data,
      (_, productId: string) => {
        return productId;
      },
      (products: Product[], productId: string) => {
        return (
          products?.find((product: Product) => product.id === productId)
            ?.webhooks || []
        );
      },
    ),
    selectIsProductWebhooksLoading: createSelector(
      (state) => state.products.data,
      (_, productId: string) => {
        return productId;
      },
      (products: Product[], productId: string) => {
        const product = products?.find(
          (product: Product) => product.id === productId,
        );

        return !product || isLoading(product.webhooksDataStatus);
      },
    ),
  },
});

const isProductsFinished = (
  state: State,
): state is {
  products: {
    data: Product[];
    status: DataStatus.finished;
  };
  webfunnelLinks: State['webfunnelLinks'];
} => {
  return state.products.status === DataStatus.finished;
};

export const {
  selectProducts,
  selectProductsDataStatus,
  selectIsProductsLoading,
  selectIsProductsFinished,
  selectProductApplications,
  selectIsProductApplicationsLoading,
  selectProductWebfunnelLinks,
  selectAllWebfunnelLinks,
  selectWebfunnelLinksDataStatus,
  selectProductWebhooks,
  selectIsProductWebhooksLoading,
} = productsSlice.selectors;
export const {
  setupProductApplications,
  setupProducts,
  addProduct,
  addProductApplication,
  updateProductApplications,
  deleteProductApplication,
  setupWebfunnelLinks,
  addWebfunnelLink,
  deleteWebfunnelLink,
  updateWebfunnelLink,
  setupProductWebhooks,
  addProductWebhook,
  deleteProductWebhook,
  reset,
} = productsSlice.actions;
